import "./Workflows.css";

function WorkflowMetric({ title, value, detail, tone = "blue" }) {
  return (
    <article className={`admin-workflow-metric tone-${tone}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function WorkflowShell({ eyebrow, title, description, metrics, toolbar, children }) {
  return (
    <section className="admin-workflow-page">
      <article className="admin-workflow-hero">
        <div className="admin-workflow-hero-copy">
          <p className="admin-workflow-eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        {toolbar ? <div className="admin-workflow-toolbar">{toolbar}</div> : null}
      </article>

      {metrics?.length ? (
        <section className="admin-workflow-metric-grid">
          {metrics.map((item) => (
            <WorkflowMetric key={item.title} {...item} />
          ))}
        </section>
      ) : null}

      {children}
    </section>
  );
}

export function AdminProductCreatePage({ locale, texts, editingProductId, onBackToProducts, productForm }) {
  const isZh = locale === "zh";

  return (
    <WorkflowShell
      eyebrow={isZh ? "商品发布工作台" : "Product Publish Workspace"}
      title={editingProductId ? (isZh ? "编辑商品详情" : "Edit Product Detail") : (isZh ? "新增商品详情" : texts.createProduct)}
      description={isZh ? "在统一后台壳层中完成主图、规格参数、交易信息和详情叙述，减少在旧表单与列表之间来回切换。" : "Complete media, specs, trade information, and detail narrative inside the unified admin shell."}
      toolbar={
        <button type="button" className="controlhub-button ghost" onClick={onBackToProducts}>
          {isZh ? "返回商品管理" : "Back to Product Management"}
        </button>
      }
      metrics={[
        { title: isZh ? "发布模式" : "Publish Mode", value: editingProductId ? (isZh ? "编辑中" : "Editing") : (isZh ? "新建中" : "Creating"), detail: isZh ? "当前编辑上下文" : "Current editor context", tone: "blue" },
        { title: isZh ? "素材建议" : "Asset Guidance", value: isZh ? "6 张主图" : "6 primary images", detail: isZh ? "支持上传或粘贴链接" : "Upload files or paste URLs", tone: "violet" },
        { title: isZh ? "详情同步" : "Detail Sync", value: isZh ? "已启用" : "Enabled", detail: isZh ? "规格与服务承诺同步前台" : "Specs and promises sync to storefront", tone: "emerald" }
      ]}
    >
      <div className="admin-workflow-form-host">{productForm}</div>
    </WorkflowShell>
  );
}

function WorkflowTable({ columns, rows, emptyText }) {
  return (
    <div className="admin-workflow-table-wrap">
      <table className="admin-workflow-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={row.key || index}>
              {columns.map((column) => (
                <td key={`${row.key || index}-${column.key}`}>{row[column.key]}</td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length}>{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AdminProcurementPage({ locale, texts, scopedWorkspaceMetrics, procurementQueueRows }) {
  const isZh = locale === "zh";

  return (
    <WorkflowShell
      eyebrow={isZh ? "采购协同中心" : "Procurement Collaboration"}
      title={texts.procurement}
      description={isZh ? "围绕采购需求、供应推进与到货前准备组织工作流，让采购动作更像独立工作台而不是简单表格。" : "Frame sourcing demand, supplier progress, and pre-arrival readiness as a dedicated workflow instead of a simple table."}
      metrics={[
        { title: isZh ? "采购待推进" : "Procurement Queue", value: scopedWorkspaceMetrics.procurementQueueCount || 0, detail: isZh ? "当前待处理采购项" : "Items currently waiting procurement", tone: "blue" },
        { title: isZh ? "可直接采购" : "Ready to Purchase", value: scopedWorkspaceMetrics.readyForLaunchCount || 0, detail: isZh ? "资料完整，可进入采购动作" : "Ready for purchase action", tone: "emerald" },
        { title: isZh ? "清单条目" : "Queue Rows", value: procurementQueueRows.length, detail: isZh ? "当前协同表条数" : "Rows in the current board", tone: "amber" }
      ]}
    >
      <article className="admin-workflow-panel">
        <div className="admin-workflow-panel-head">
          <div>
            <h4>{isZh ? "采购执行建议" : "Procurement Workbench"}</h4>
            <p>{isZh ? "继续复用商品主数据，但用更明确的运营层次呈现采购建议。" : "Keep reusing product master data but present purchase recommendations with clearer hierarchy."}</p>
          </div>
        </div>
        <WorkflowTable
          columns={[
            { key: "sku", label: texts.sku },
            { key: "name", label: texts.name },
            { key: "suggestion", label: isZh ? "采购建议" : "Purchase Suggestion" },
            { key: "status", label: isZh ? "当前状态" : "Current Status" }
          ]}
          rows={procurementQueueRows.map((item) => ({
            key: `procurement-${item.id}`,
            sku: item.sku || "-",
            name: item.name?.en || item.name || "-",
            suggestion: item.suggestion === "issue_purchase_request" ? (isZh ? "可发起采购" : "Issue purchase request") : (isZh ? "先补齐成本/报价" : "Complete costing first"),
            status: item.procurementStatus === "ready_to_purchase" ? (isZh ? "待下单" : "Pending PO") : (isZh ? "待核价" : "Pending costing")
          }))}
          emptyText={texts.none}
        />
      </article>
    </WorkflowShell>
  );
}

export function AdminQualityPage({ locale, texts, scopedWorkspaceMetrics, qualityQueueRows }) {
  const isZh = locale === "zh";

  return (
    <WorkflowShell
      eyebrow={isZh ? "质检与方案中心" : "QA and Solution Center"}
      title={texts.quality}
      description={isZh ? "跟进样品确认、来料验收和发货前质检，把质量动作从普通列表升级为更清晰的执行面板。" : "Track samples, inbound inspection, and pre-shipment QA in a clearer execution workspace."}
      metrics={[
        { title: isZh ? "待质检商品" : "Inspection Queue", value: scopedWorkspaceMetrics.qualityQueueCount || 0, detail: isZh ? "待继续推进的质检项" : "Quality items still in progress", tone: "blue" },
        { title: isZh ? "执行清单" : "Execution Rows", value: qualityQueueRows.length, detail: isZh ? "当前质检表条目" : "Rows in the QA board", tone: "violet" },
        { title: isZh ? "流程重点" : "Primary Focus", value: isZh ? "样品 + 来料" : "Samples + inbound", detail: isZh ? "优先确认样品和来料环节" : "Prioritize sampling and inbound checks", tone: "emerald" }
      ]}
    >
      <article className="admin-workflow-panel">
        <div className="admin-workflow-panel-head">
          <div>
            <h4>{isZh ? "质检任务清单" : "QA Task Queue"}</h4>
            <p>{isZh ? "保留原有数据结构，提升表格层次和卡片可读性。" : "Keep the current data structure and improve readability with a better table surface."}</p>
          </div>
        </div>
        <WorkflowTable
          columns={[
            { key: "sku", label: texts.sku },
            { key: "name", label: texts.name },
            { key: "inspectionStage", label: isZh ? "质检阶段" : "Inspection Stage" },
            { key: "qualityStatus", label: isZh ? "发运状态" : "Release Status" }
          ]}
          rows={qualityQueueRows.map((item) => ({
            key: `quality-${item.id}`,
            sku: item.sku || "-",
            name: item.name?.en || item.name || "-",
            inspectionStage: item.inspectionStage,
            qualityStatus: item.qualityStatus
          }))}
          emptyText={texts.none}
        />
      </article>
    </WorkflowShell>
  );
}

export function AdminLogisticsPage({ locale, texts, scopedWorkspaceMetrics, logisticsQueueRows }) {
  const isZh = locale === "zh";

  return (
    <WorkflowShell
      eyebrow={isZh ? "物流协同中心" : "Logistics Coordination"}
      title={texts.logistics}
      description={isZh ? "围绕发运准备、装箱资料和物流追踪协同，让物流页保持与订单、采购页一致的后台体验。" : "Coordinate shipment readiness, packing docs, and tracking inside a page that matches the rest of the admin shell."}
      metrics={[
        { title: isZh ? "在途订单" : "In Transit", value: scopedWorkspaceMetrics.inTransitCount || 0, detail: isZh ? "当前运输中的发运任务" : "Shipments currently in transit", tone: "blue" },
        { title: isZh ? "协同清单" : "Coordination Rows", value: logisticsQueueRows.length, detail: isZh ? "当前物流协同条目" : "Rows in the logistics board", tone: "violet" },
        { title: isZh ? "追踪重点" : "Tracking Focus", value: isZh ? "节点同步" : "Milestone sync", detail: isZh ? "重点跟进运单与状态变化" : "Follow tracking numbers and status updates", tone: "amber" }
      ]}
    >
      <article className="admin-workflow-panel">
        <div className="admin-workflow-panel-head">
          <div>
            <h4>{isZh ? "物流协同清单" : "Logistics Coordination List"}</h4>
            <p>{isZh ? "沿用原有运单数据，但提升页面结构和响应式可读性。" : "Keep the existing tracking data while improving layout hierarchy and responsiveness."}</p>
          </div>
        </div>
        <WorkflowTable
          columns={[
            { key: "orderId", label: texts.orderId },
            { key: "userId", label: texts.userId },
            { key: "status", label: texts.status },
            { key: "trackingNumber", label: isZh ? "运单号" : "Tracking Number" }
          ]}
          rows={logisticsQueueRows.map((item) => ({
            key: `logistics-${item.orderId}`,
            orderId: item.orderId,
            userId: item.userId,
            status: item.status,
            trackingNumber: item.trackingNumber
          }))}
          emptyText={texts.none}
        />
      </article>
    </WorkflowShell>
  );
}